// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import {
  Bell,
  CheckCheck,
  Loader2,
  Settings2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/lib/auth/useAuth'
import {
  useInAppNotifications,
  useInAppNotificationsWebSocket,
  useInAppUnreadCount,
  useMarkAllInAppNotificationsRead,
  useMarkInAppNotificationRead,
} from '@/lib/hooks/useAccountSettings'
import { cn } from '@/lib/utils/cn'
import { ROUTES } from '@/constants/routes'
import type { InAppNotificationItem } from '@/types'
import {
  InAppNotificationBody,
  severityBar,
} from '@/components/notifications/in-app-notification-presentation'

function NotificationRow({
  n,
  onActivate,
}: {
  n: InAppNotificationItem
  onActivate: (n: InAppNotificationItem) => void
}) {
  const unread = !n.readAt

  return (
    <DropdownMenuItem
      className={cn(
        'cursor-pointer flex gap-3 rounded-md px-2 py-2.5 h-auto min-h-0 items-start',
        severityBar(n.severity),
        unread ? 'bg-brand/5' : 'opacity-90',
      )}
      onSelect={(e) => {
        e.preventDefault()
        onActivate(n)
      }}
    >
      <InAppNotificationBody n={n} />
    </DropdownMenuItem>
  )
}

export function NotificationCenter() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [open, setOpen] = useState(false)
  useInAppNotificationsWebSocket(isAuthenticated && !authLoading)
  const { data: unreadData } = useInAppUnreadCount()
  const unread = unreadData?.unread ?? 0
  const q = useInAppNotifications(open)
  const markRead = useMarkInAppNotificationRead()
  const markAll = useMarkAllInAppNotificationsRead()

  const items = useMemo(
    () => q.data?.pages.flatMap((p) => p.notifications) ?? [],
    [q.data?.pages],
  )

  const handleActivate = (n: InAppNotificationItem) => {
    if (!n.readAt) {
      void markRead.mutateAsync(n.id)
    }
    if (n.actionPath) {
      navigate(n.actionPath)
    } else {
      navigate(ROUTES.NOTIFICATIONS)
    }
    setOpen(false)
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[min(100vw-2rem,22rem)] p-0">
        <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
          <span className="text-xs font-semibold text-ink">Notifications</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1 px-2"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => void markAll.mutateAsync()}
          >
            {markAll.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCheck className="h-3.5 w-3.5" />
            )}
            Mark all read
          </Button>
        </div>

        <div className="max-h-[min(24rem,70vh)] overflow-y-auto py-1">
          {q.isLoading && (
            <div className="flex justify-center py-10 text-ink-muted">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
          {q.isError && (
            <p className="px-3 py-6 text-center text-xs text-danger">Could not load notifications.</p>
          )}
          {!q.isLoading && !q.isError && items.length === 0 && (
            <p className="px-3 py-8 text-center text-xs text-ink-muted">You&apos;re all caught up.</p>
          )}
          {!q.isLoading &&
            !q.isError &&
            items.map((n) => <NotificationRow key={n.id} n={n} onActivate={handleActivate} />)}
        </div>

        {q.hasNextPage && (
          <div className="border-t border-line p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs"
              disabled={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
            >
              {q.isFetchingNextPage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Load older'
              )}
            </Button>
          </div>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-xs gap-2 py-2"
          onSelect={(e) => {
            e.preventDefault()
            navigate(ROUTES.NOTIFICATIONS)
            setOpen(false)
          }}
        >
          <Bell className="h-3.5 w-3.5" />
          View all notifications
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-xs gap-2 py-2"
          onSelect={(e) => {
            e.preventDefault()
            navigate(ROUTES.SETTINGS_NOTIFICATIONS)
            setOpen(false)
          }}
        >
          <Settings2 className="h-3.5 w-3.5" />
          Notification settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
